import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  X,
  MoreVertical,
  Check,
  ArrowRight,
  AlertCircle,
  Loader2,
  RefreshCw,
  Crown,
} from "lucide-react";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";
import { openPaytmCheckout } from "../lib/paytmService";

export default function YourSubscriptionModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [apiFeatures, setApiFeatures] = useState([]);
  const [billingCycle, setBillingCycle] = useState("quarterly");
  const [showMenu, setShowMenu] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedCancelReason, setSelectedCancelReason] = useState("");
  const [otherCancelReason, setOtherCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgradePlanId, setSelectedUpgradePlanId] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const [toast, setToast] = useState(null);

  const menuRef = useRef(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Close 3-dots menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  // Load subscription and plans when modal opens
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      loadSubscriptionData();
    } else {
      document.body.style.overflow = "auto";
      setShowMenu(false);
      setShowCancelModal(false);
      setShowUpgradeModal(false);
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const loadSubscriptionData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch current active subscription
      const subRes = await fetchWithAuth(API_ENDPOINTS.PAYTM_SUBSCRIPTION);
      if (subRes.ok) {
        const subData = await subRes.json();
        if (
          !subData ||
          subData.statusCode === 404 ||
          subData.error === "Not Found" ||
          !subData.id
        ) {
          setSubscription(null);
        } else {
          setSubscription(subData);
          const isAnn =
            subData.billingPeriod?.toLowerCase() === "annual" ||
            subData.frequencyUnit?.toUpperCase() === "YEAR" ||
            subData.name?.toLowerCase().includes("annual") ||
            subData.type?.toLowerCase().includes("annual");
          setBillingCycle(isAnn ? "annual" : "quarterly");
        }
      } else {
        setSubscription(null);
      }

      // 2. Fetch available plans for upgrade comparison directly from API
      try {
        const plansRes = await fetchWithAuth(API_ENDPOINTS.PAYTM_SUBSCRIPTION_PLANS);
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          setAvailablePlans(Array.isArray(plansData?.plans) ? plansData.plans : []);
          if (Array.isArray(plansData?.features)) {
            setApiFeatures(plansData.features);
          }
        } else {
          setAvailablePlans([]);
        }
      } catch (err) {
        console.warn("Could not load plans list from API:", err);
        setAvailablePlans([]);
      }
    } catch (err) {
      console.error("Error loading subscription:", err);
      setError("Couldn't load subscription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    const reason = (
      selectedCancelReason === "Other" ? otherCancelReason : selectedCancelReason
    ).trim();

    if (!reason) {
      showToast("Please choose or enter a cancellation reason", "error");
      return;
    }

    setCancelling(true);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.PAYTM_SUBSCRIPTION_CANCEL, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });

      if (res.ok) {
        showToast("Subscription cancelled successfully", "success");
        setShowCancelModal(false);
        setSubscription(null);
        if (onSuccess) onSuccess();
      } else {
        const errData = await res.json().catch(() => ({}));
        showToast(errData.message || "Failed to cancel subscription", "error");
      }
    } catch (err) {
      console.error("Cancel subscription error:", err);
      showToast("Network error cancelling subscription", "error");
    } finally {
      setCancelling(false);
    }
  };

  const handleUpgradeSubscription = async () => {
    if (!selectedUpgradePlanId) {
      showToast("Please select a plan to proceed", "error");
      return;
    }

    setUpgrading(true);
    try {
      let userPhone =
        localStorage.getItem("number") ||
        localStorage.getItem("phone") ||
        localStorage.getItem("phoneNumber") ||
        localStorage.getItem("mobile") ||
        sessionStorage.getItem("number") ||
        subscription?.phoneNumber ||
        subscription?.phone ||
        subscription?.phone_number ||
        subscription?.mobile ||
        "";

      // If missing from storage, fetch from partner profile
      if (!userPhone) {
        try {
          const profRes = await fetchWithAuth(API_ENDPOINTS.PARTNER);
          if (profRes.ok) {
            const profData = await profRes.json();
            if (profData?.phone_number) {
              userPhone = profData.phone_number;
              localStorage.setItem("number", userPhone);
            }
          }
        } catch (e) {
          console.warn("Could not fetch phone from partner profile:", e);
        }
      }

      const isUpgrading = Boolean(
        subscription &&
        subscription.id &&
        (subscription.status === "active" ||
          subscription.status === "subscribed" ||
          subscription.status === "authenticated")
      );
      const endpoint = isUpgrading
        ? API_ENDPOINTS.PAYTM_SUBSCRIPTION_UPGRADE
        : API_ENDPOINTS.PAYTM_SUBSCRIPTION;
      const method = isUpgrading ? "PUT" : "POST";
      const bodyPayload = isUpgrading
        ? {
            newPlanId: selectedUpgradePlanId.trim(),
            ...(userPhone ? { mobile: userPhone, phoneNumber: userPhone, phone: userPhone } : {}),
          }
        : {
            planId: selectedUpgradePlanId.trim(),
            ...(userPhone ? { mobile: userPhone, phoneNumber: userPhone, phone: userPhone } : {}),
          };

      console.log(`Processing subscription via ${method} ${endpoint} with:`, bodyPayload);

      const res = await fetchWithAuth(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const errMsg =
          errData.message ||
          errData.error ||
          `Backend returned ${res.status}: Failed to process subscription request.`;
        console.error("Paytm plan subscription request failed:", res.status, errData);
        showToast(errMsg, "error");
        setUpgrading(false);
        return;
      }

      const data = await res.json();
      console.log("Paytm subscription response:", data);

      // Check response data for mobile as well
      const effectiveMobile =
        userPhone ||
        data?.mobile ||
        data?.mobileNumber ||
        data?.phoneNumber ||
        data?.phone ||
        data?.custPhone ||
        "";

      if (effectiveMobile && !localStorage.getItem("number")) {
        localStorage.setItem("number", effectiveMobile);
      }

      // If backend indicates a new mandate or payment is needed, launch Paytm CheckoutJS
      if (data.needsNewMandate === true || (!isUpgrading && data.txnToken) || data.txnToken) {
        if (!data.id || !data.txnToken || !data.mid) {
          const errMsg = "Payment parameters missing from server response. Please retry.";
          showToast(errMsg, "error");
          setUpgrading(false);
          return;
        }

        const mandateAmount =
          data.currentAmount ||
          data.mandateMaxAmount ||
          50000;

        await openPaytmCheckout({
          orderId: data.id,
          txnToken: data.txnToken,
          amount: mandateAmount,
          mid: data.mid,
          callbackUrl: data.callbackUrl,
          mobileNumber: effectiveMobile,
          onSuccess: async () => {
            console.log("Paytm upgrade checkout success, verifying mandate orderId:", data.id);
            try {
              const verifyRes = await fetchWithAuth(
                API_ENDPOINTS.PAYTM_SUBSCRIPTION_VERIFY,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ orderId: data.id }),
                }
              );

              if (verifyRes.ok) {
                showToast(
                  isUpgrading
                    ? "Plan upgraded successfully!"
                    : "Subscription activated successfully!",
                  "success"
                );
                setShowUpgradeModal(false);
                loadSubscriptionData();
                if (onSuccess) onSuccess();
              } else {
                const verifyErrData = await verifyRes.json().catch(() => ({}));
                const verifyErrMsg =
                  verifyErrData.message ||
                  "Payment completed, but verification failed on server.";
                showToast(verifyErrMsg, "error");
              }
            } catch (vErr) {
              console.error("Verification error during upgrade:", vErr);
              showToast("Verification request failed. Please check status.", "error");
            } finally {
              setUpgrading(false);
            }
          },
          onError: (err) => {
            console.error("Paytm transaction error during upgrade:", err);
            const errMsg =
              err?.message || "Payment cancelled or failed. Please try again.";
            showToast(errMsg, "error");
            setUpgrading(false);
          },
          onClose: () => {
            console.log("Paytm checkout popup closed");
            setUpgrading(false);
          },
        });
      } else {
        // Upgrade applied instantly within existing mandate
        showToast("Plan updated successfully!", "success");
        setShowUpgradeModal(false);
        loadSubscriptionData();
        if (onSuccess) onSuccess();
        setUpgrading(false);
      }
    } catch (err) {
      console.error("Upgrade error:", err);
      showToast(err?.message || "Error processing plan request", "error");
      setUpgrading(false);
    }
  };

  // Format Helpers
  const formatMemberTill = (isoDate) => {
    if (!isoDate) return "Active";
    try {
      const d = new Date(isoDate);
      return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    } catch {
      return "Active";
    }
  };

  const formatAmount = (paiseOrRupees) => {
    if (paiseOrRupees == null) return "0";
    const amt = typeof paiseOrRupees === "number" ? paiseOrRupees : parseInt(paiseOrRupees, 10);
    return amt.toLocaleString("en-IN");
  };

  const isAnnual = billingCycle === "annual";

  const displayPlanName =
    subscription?.type ||
    subscription?.planType ||
    subscription?.name ||
    "Subscription";

  const rawAmount =
    subscription?.currentAmount ?? subscription?.mandateMaxAmount ?? 0;
  // If amount is in paise (> 100), divide by 100, otherwise display as is
  const displayRupees =
    rawAmount >= 100 ? Math.round(rawAmount / 100) : rawAmount;

  const status = (subscription?.status || "").toLowerCase();
  const isActive = Boolean(
    subscription &&
    subscription.id &&
    (status === "active" || status === "subscribed" || status === "authenticated")
  );

  // Usage info
  const usage = subscription?.usage;
  const maxEarnings = usage?.maxEarnings ?? subscription?.maxEarnings ?? null;
  const remainingEarnings = usage?.remainingEarnings ?? maxEarnings;
  const earnedAmount = usage?.earnings ?? 0;

  const maxRequests = usage?.maxRequests ?? subscription?.maxRequests ?? null;
  const remainingRequests = usage?.remainingRequests ?? maxRequests;
  const requestsUsed = usage?.requests ?? 0;

  // Features list strictly from subscription or API response
  const featuresList =
    Array.isArray(subscription?.features) && subscription.features.length > 0
      ? subscription.features
      : apiFeatures;

  // Filter plans to show candidates
  const currentAmount =
    subscription?.currentAmount ?? subscription?.mandateMaxAmount ?? 0;
  const currentPlanId = subscription?.planId;

  const upgradeablePlans = availablePlans
    .map((plan) => ({
      ...plan,
      interval: isAnnual ? plan.annual : plan.quarterly,
    }))
    .filter((p) => {
      if (!p.interval || !p.interval.id) return false;
      if (isActive && currentPlanId && p.interval.id === currentPlanId) return false;
      if (isActive && currentAmount > 0 && p.interval.amount != null) {
        return p.interval.amount > currentAmount;
      }
      return true;
    });

  // Check if current subscription is already the highest pack
  const planType = (
    subscription?.planType ||
    subscription?.type ||
    ""
  ).toLowerCase();
  const planName = (subscription?.name || "").toLowerCase();

  const isHighestPack = Boolean(
    isActive && (
      planType === "expert" ||
      planName.includes("expert") ||
      planName.includes("high gigs") ||
      (availablePlans.length > 0 && upgradeablePlans.length === 0) ||
      (availablePlans.length > 0 &&
        currentAmount > 0 &&
        availablePlans.every((p) => {
          const amt = isAnnual ? p.annual?.amount : p.quarterly?.amount;
          return amt == null || currentAmount >= amt;
        }))
    )
  );

  // Auto-select first available plan whenever plans or cycle changes
  useEffect(() => {
    if (upgradeablePlans.length > 0) {
      if (
        !selectedUpgradePlanId ||
        !upgradeablePlans.some((p) => p.interval?.id === selectedUpgradePlanId)
      ) {
        setSelectedUpgradePlanId(upgradeablePlans[0].interval.id);
      }
    }
  }, [upgradeablePlans, selectedUpgradePlanId]);

  const selectedPlanObj =
    upgradeablePlans.find((p) => p.interval?.id === selectedUpgradePlanId) ||
    upgradeablePlans[0];
  const selectedPlanPrice =
    selectedPlanObj?.interval?.amount != null
      ? Math.round(selectedPlanObj.interval.amount / 100)
      : 0;

  const cancellationReasons = [
    "Too expensive",
    "Not using the features",
    "Found a better alternative",
    "Technical issues",
    "Poor customer service",
    "No longer need the service",
    "Temporary financial constraints",
    "Switching to free version",
    "Other",
  ];

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4 md:p-6 animate-fadeIn">
      {/* Toast Alert */}
      {toast && (
        <div
          className={`fixed top-5 left-1/2 -translate-x-1/2 z-[150] px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium flex items-center gap-2 transition-all ${
            toast.type === "error" ? "bg-red-600" : "bg-emerald-600"
          }`}
        >
          {toast.type === "error" ? (
            <AlertCircle className="w-4 h-4" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Main Card Container - Responsive width on laptops & desktops */}
      <div className="bg-[#F8F9FB] rounded-3xl shadow-2xl w-full max-w-md sm:max-w-xl md:max-w-2xl lg:max-w-3xl max-h-[92vh] flex flex-col overflow-hidden relative border border-gray-150 animate-slideUp">
        {/* Top App Bar */}
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-100 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center text-gray-700"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-gray-900 font-sans tracking-tight">
                Your Subscription
              </h2>
              <p className="text-[11px] text-gray-500 font-medium hidden sm:block">
                {isActive
                  ? "Manage your active membership & limits"
                  : "Choose a plan to activate your membership"}
              </p>
            </div>
          </div>

          {/* 3-dots Menu for Cancel Subscription (Only if active) */}
          {isActive && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="w-9 h-9 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center text-purple-700"
                aria-label="Options menu"
              >
                <MoreVertical className="w-5 h-5" />
              </button>

              {showMenu && (
                <div className="absolute right-0 top-10 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-20 animate-fadeIn">
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowCancelModal(true);
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 font-medium transition-colors"
                  >
                    Cancel Subscription
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5">
          {loading ? (
            <div className="py-24 flex flex-col items-center justify-center gap-3 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin text-[#6720A4]" />
              <p className="text-sm font-medium">Loading your subscription...</p>
            </div>
          ) : error ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3 text-center px-4">
              <AlertCircle className="w-10 h-10 text-red-500" />
              <p className="text-sm font-medium text-gray-700">{error}</p>
              <button
                onClick={loadSubscriptionData}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#6720A4] text-white text-sm font-semibold hover:bg-[#551887] transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          ) : !isActive ? (
            /* Clear & Beautiful "No Active Subscription" Screen */
            <div className="space-y-6">
              {/* Premium Gradient Hero Banner */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #4A1278 0%, #6720A4 50%, #883163 100%)",
                }}
                className="rounded-3xl p-6 sm:p-7 text-white shadow-xl relative overflow-hidden"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/30 text-xs font-bold tracking-wide">
                    <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                    NO ACTIVE SUBSCRIPTION
                  </span>

                  <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-xs flex items-center justify-center border border-white/20">
                    <Crown className="w-5 h-5 text-amber-300" />
                  </div>
                </div>

                <div className="mt-4">
                  <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-white">
                    Unlock Full DJ &amp; Artist Features
                  </h1>
                  <p className="text-xs sm:text-sm text-white/85 font-medium mt-1.5 max-w-xl leading-relaxed">
                    You currently do not have an active membership. Subscribe to a plan below to host live events, accept paid fan song requests with 0% commission, and enable instant bank payouts.
                  </p>
                </div>

                {/* Key Benefits Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-5 pt-4 border-t border-white/15 text-xs text-white/95">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-emerald-400/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-emerald-300 stroke-[3]" />
                    </div>
                    <span className="font-medium">0% commission on fan song requests &amp; tips</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-emerald-400/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-emerald-300 stroke-[3]" />
                    </div>
                    <span className="font-medium">Host live gigs &amp; interactive venue queues</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-emerald-400/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-emerald-300 stroke-[3]" />
                    </div>
                    <span className="font-medium">Direct payouts straight to your bank account</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-emerald-400/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-emerald-300 stroke-[3]" />
                    </div>
                    <span className="font-medium">Instant real-time fan request notifications</span>
                  </div>
                </div>
              </div>

              {/* Plan Picker Section with Cycle Toggle */}
              <div className="space-y-3.5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      SELECT MEMBERSHIP PLAN
                    </h3>
                    <p className="text-xs text-gray-500">Pick the plan that best fits your gig and audience size</p>
                  </div>

                  {/* Billing Cycle Switcher */}
                  <div className="inline-flex p-1 bg-gray-200/80 rounded-xl border border-gray-200 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => setBillingCycle("quarterly")}
                      className={`px-3 py-1.5 rounded-lg transition-all ${
                        !isAnnual
                          ? "bg-white text-gray-900 shadow-sm font-bold"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Quarterly
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillingCycle("annual")}
                      className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                        isAnnual
                          ? "bg-[#6720A4] text-white shadow-sm font-bold"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <span>Annual</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400 text-gray-900 font-extrabold uppercase tracking-wide">
                        SAVE
                      </span>
                    </button>
                  </div>
                </div>

                {/* Plans Grid (3 columns on larger screens) */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {upgradeablePlans && upgradeablePlans.length > 0 ? (
                    upgradeablePlans.map((plan, idx) => {
                      const planInterval = plan.interval;
                      const planId = planInterval?.id;
                      const price = (planInterval?.amount || 0) / 100;
                      const isSelected = selectedUpgradePlanId === planId;
                      const isPopular = (plan.type || "").toLowerCase().includes("performer");

                      return (
                        <div
                          key={planId || idx}
                          onClick={() => setSelectedUpgradePlanId(planId)}
                          className={`relative p-4 rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between ${
                            isSelected
                              ? "border-[#6720A4] bg-purple-50/70 shadow-md ring-2 ring-purple-300/40"
                              : "border-gray-200 hover:border-gray-300 bg-white shadow-xs"
                          }`}
                        >
                          {isPopular && (
                            <span className="absolute -top-2.5 right-3 bg-[#6720A4] text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                              Popular
                            </span>
                          )}
                          <div>
                            <div className="flex items-center justify-between">
                              <h4 className="font-extrabold text-sm text-gray-900">
                                {plan.type || planInterval?.name || `Plan ${idx + 1}`}
                              </h4>
                              <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  isSelected
                                    ? "border-[#6720A4] bg-[#6720A4]"
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {isSelected && (
                                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                )}
                              </div>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              {isAnnual ? "Annual Billing" : "Quarterly Billing"}
                            </p>

                            <div className="mt-3">
                              <span className="font-extrabold text-xl text-[#6720A4]">
                                ₹{formatAmount(price)}
                              </span>
                              <span className="text-[11px] text-gray-400 font-medium ml-1">
                                {isAnnual ? "/year" : "/quarter"}
                              </span>
                            </div>

                            {planInterval?.maxEarnings && (
                              <p className="text-[11px] text-purple-800 font-semibold mt-2.5 bg-purple-100/70 rounded-lg px-2 py-1 inline-block">
                                Earn up to ₹{formatAmount(planInterval.maxEarnings)}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-span-3 p-6 text-center text-sm text-gray-500 bg-white rounded-2xl border border-gray-200">
                      Loading available plans...
                    </div>
                  )}
                </div>
              </div>

              {/* What's Included / Core Features */}
              {featuresList && featuresList.length > 0 && (
                <div className="space-y-2.5 pt-1">
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    ALL PLANS INCLUDE
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {featuresList.map((feature, idx) => (
                      <div
                        key={idx}
                        className="bg-white rounded-xl px-3.5 py-2.5 border border-gray-150 shadow-xs flex items-center gap-2.5"
                      >
                        <div className="w-5 h-5 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                          <Check className="w-3.5 h-3.5 text-[#6720A4] stroke-[2.5]" />
                        </div>
                        <span className="text-xs font-semibold text-gray-800 leading-snug">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Active Subscription View */
            <>
              {/* Premium Gradient Hero Card */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #581694 0%, #6F1E85 45%, #883163 100%)",
                }}
                className="rounded-3xl p-6 sm:p-7 text-white shadow-xl relative overflow-hidden"
              >
                {/* Top Row: Status Chip & Watermark Icon */}
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-950/40 text-emerald-400 border border-emerald-500/20 text-xs font-bold tracking-wide">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                    ACTIVE
                  </span>

                  {/* Top Right Watermark Icon */}
                  <div className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center opacity-70">
                    <RefreshCw className="w-4 h-4 text-white" />
                  </div>
                </div>

                {/* Plan Name & Interval */}
                <div className="mt-4">
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                    {displayPlanName}
                  </h1>
                  <p className="text-xs sm:text-sm text-white/80 font-medium mt-0.5">
                    {isAnnual ? "Annual Membership" : "Quarterly Membership"}
                  </p>
                </div>

                {/* Subtle Divider */}
                <div className="border-t border-white/20 my-5" />

                {/* Amount Paid & Member Till */}
                <div className="flex items-end justify-between">
                  <div>
                    <span className="block text-[10px] font-bold text-white/60 tracking-wider uppercase mb-1">
                      AMOUNT PAID
                    </span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl sm:text-3xl font-extrabold text-white">
                        ₹{formatAmount(displayRupees)}
                      </span>
                      <span className="text-xs text-white/80 font-medium">
                        {isAnnual ? "/year" : "/3 months"}
                      </span>
                    </div>
                  </div>

                  <div className="text-right">
                    <span className="block text-[10px] font-bold text-white/60 tracking-wider uppercase mb-1">
                      MEMBER TILL
                    </span>
                    <span className="text-sm sm:text-base font-bold text-white">
                      {formatMemberTill(
                        subscription?.expiryDate ||
                          subscription?.startDate ||
                          subscription?.createdAt
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* REMAINING BALANCE & USAGE SECTION (2 columns on laptop screens) */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  REMAINING BALANCE &amp; USAGE
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  {/* Remaining Earnings Limit Card */}
                  <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-150 shadow-xs space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <span className="text-lg">💰</span>
                        <span className="text-xs sm:text-sm font-bold text-gray-900">
                          Earnings Limit
                        </span>
                      </div>
                      <span className="text-xs font-bold text-[#6720A4] text-right">
                        {maxEarnings != null
                          ? `₹${formatAmount(remainingEarnings)} / ₹${formatAmount(maxEarnings)}`
                          : "Unlimited Earning"}
                      </span>
                    </div>

                    {/* Progress Line */}
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-[#6720A4] h-2 rounded-full transition-all duration-500"
                        style={{
                          width:
                            maxEarnings && maxEarnings > 0
                              ? `${Math.max(
                                  5,
                                  Math.min(
                                    100,
                                    Math.round((remainingEarnings / maxEarnings) * 100)
                                  )
                                )}%`
                              : "100%",
                        }}
                      />
                    </div>

                    <p className="text-[11px] text-gray-400 font-medium">
                      {maxEarnings != null
                        ? `₹${formatAmount(earnedAmount)} earned in current cycle`
                        : "No capping limit on earnings"}
                    </p>
                  </div>

                  {/* Remaining Fan Requests Card */}
                  <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-150 shadow-xs space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <span className="text-lg">🎶</span>
                        <span className="text-xs sm:text-sm font-bold text-gray-900">
                          Fan Requests
                        </span>
                      </div>
                      <span className="text-xs font-bold text-[#6720A4] text-right">
                        {maxRequests != null
                          ? `${remainingRequests} / ${maxRequests}`
                          : "Unlimited Requests"}
                      </span>
                    </div>

                    {/* Progress Line */}
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-[#6720A4] h-2 rounded-full transition-all duration-500"
                        style={{
                          width:
                            maxRequests && maxRequests > 0
                              ? `${Math.max(
                                  5,
                                  Math.min(
                                    100,
                                    Math.round((remainingRequests / maxRequests) * 100)
                                  )
                                )}%`
                              : "100%",
                        }}
                      />
                    </div>

                    <p className="text-[11px] text-gray-400 font-medium">
                      {maxRequests != null
                        ? `${requestsUsed} requests processed in cycle`
                        : "No limit on fan requests"}
                    </p>
                  </div>
                </div>
              </div>

              {/* WHAT'S INCLUDED SECTION (2 columns on laptop screens) */}
              <div className="space-y-2.5 pb-2">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  WHAT&apos;S INCLUDED
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {featuresList.map((feature, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-xl px-4 py-3 border border-gray-150 shadow-xs flex items-center gap-3"
                    >
                      <div className="w-5 h-5 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                        <Check className="w-3.5 h-3.5 text-[#6720A4] stroke-[2.5]" />
                      </div>
                      <span className="text-xs sm:text-[13px] font-semibold text-gray-800 leading-snug">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Sticky Action Footer */}
        {isActive ? (
          /* Active user: Upgrade Plan Button (Hidden if highest pack) */
          !isHighestPack && (
            <div className="p-4 sm:p-5 bg-white/95 backdrop-blur-md border-t border-gray-100">
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="w-full bg-[#6720A4] hover:bg-[#57198C] active:scale-[0.98] text-white font-bold py-3.5 px-6 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-300/40 transition-all text-sm sm:text-base"
              >
                <span>Upgrade Plan</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )
        ) : (
          /* Unsubscribed user: Direct Subscribe CTA Button */
          <div className="p-4 sm:p-5 bg-white/95 backdrop-blur-md border-t border-gray-100">
            <button
              type="button"
              disabled={upgrading || !selectedUpgradePlanId || upgradeablePlans.length === 0}
              onClick={handleUpgradeSubscription}
              className="w-full bg-[#6720A4] hover:bg-[#57198C] active:scale-[0.98] text-white font-bold py-3.5 px-6 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-300/40 transition-all text-sm sm:text-base disabled:opacity-50"
            >
              {upgrading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing Checkout...</span>
                </>
              ) : (
                <>
                  <span>
                    Subscribe to {selectedPlanObj?.type || "Membership"} • ₹{formatAmount(selectedPlanPrice)}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Cancellation Submodal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm md:max-w-md w-full p-6 space-y-4 border border-gray-100 animate-slideUp">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">
                Cancel Subscription
              </h3>
              <button
                onClick={() => setShowCancelModal(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 text-gray-500 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed">
              We&apos;re sorry to see you go! Please let us know why you are
              cancelling:
            </p>

            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {cancellationReasons.map((reason) => (
                <label
                  key={reason}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-purple-50/50 cursor-pointer text-xs font-medium text-gray-700 border border-gray-100"
                >
                  <input
                    type="radio"
                    name="cancelReason"
                    value={reason}
                    checked={selectedCancelReason === reason}
                    onChange={(e) => setSelectedCancelReason(e.target.value)}
                    className="accent-[#6720A4] w-3.5 h-3.5"
                  />
                  <span>{reason}</span>
                </label>
              ))}
            </div>

            {selectedCancelReason === "Other" && (
              <textarea
                rows={2}
                value={otherCancelReason}
                onChange={(e) => setOtherCancelReason(e.target.value)}
                placeholder="Please describe reason..."
                className="w-full text-xs p-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#6720A4]"
              />
            )}

            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Keep Plan
              </button>
              <button
                type="button"
                disabled={cancelling}
                onClick={handleCancelSubscription}
                className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-all disabled:opacity-50"
              >
                {cancelling ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Submodal (for active users) */}
      {showUpgradeModal && !isHighestPack && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md md:max-w-lg lg:max-w-xl w-full p-6 space-y-4 border border-gray-100 animate-slideUp">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  {isActive ? "Upgrade Your Plan" : "Choose Membership Plan"}
                </h3>
                <p className="text-xs text-gray-500">
                  {isActive
                    ? "Unlock higher earnings and fan request capacity"
                    : "Zero commission on fan requests & direct bank payouts"}
                </p>
              </div>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 text-gray-500 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cycle Toggle inside Upgrade modal */}
            <div className="flex items-center justify-end">
              <div className="inline-flex p-1 bg-gray-100 rounded-xl border border-gray-200 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setBillingCycle("quarterly")}
                  className={`px-3 py-1 rounded-lg transition-all ${
                    !isAnnual
                      ? "bg-white text-gray-900 shadow-xs font-bold"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Quarterly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle("annual")}
                  className={`px-3 py-1 rounded-lg transition-all flex items-center gap-1.5 ${
                    isAnnual
                      ? "bg-[#6720A4] text-white shadow-xs font-bold"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <span>Annual</span>
                  <span className="text-[9px] px-1 py-0.2 rounded-full bg-amber-400 text-gray-900 font-extrabold uppercase">
                    SAVE
                  </span>
                </button>
              </div>
            </div>

            <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
              {upgradeablePlans && upgradeablePlans.length > 0 ? (
                upgradeablePlans.map((plan, idx) => {
                  const planInterval = plan.interval;
                  const planId = planInterval?.id;
                  const price = (planInterval?.amount || 0) / 100;
                  const isSelected = selectedUpgradePlanId === planId;

                  return (
                    <div
                      key={planId || idx}
                      onClick={() => setSelectedUpgradePlanId(planId)}
                      className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                        isSelected
                          ? "border-[#6720A4] bg-purple-50/60 shadow-xs ring-2 ring-purple-200"
                          : "border-gray-150 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <div>
                        <span className="font-bold text-sm text-gray-900 block">
                          {plan.type || planInterval?.name || `Plan ${idx + 1}`}
                        </span>
                        <span className="text-[11px] text-gray-500">
                          {isAnnual ? "Annual Billing" : "Quarterly Billing"}
                        </span>
                        {planInterval?.maxEarnings && (
                          <span className="text-xs text-purple-700 font-semibold block mt-1">
                            Earn up to ₹{planInterval.maxEarnings.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="font-extrabold text-base text-[#6720A4]">
                          ₹{formatAmount(price)}
                        </span>
                        <span className="text-[11px] text-gray-400 block">
                          {isAnnual ? "/year" : "/quarter"}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-4 rounded-2xl bg-purple-50 text-center text-xs text-purple-900 border border-purple-100">
                  You are already on the highest available plan for this billing cycle.
                </div>
              )}
            </div>

            <div className="pt-2">
              <button
                type="button"
                disabled={upgrading || !selectedUpgradePlanId || upgradeablePlans.length === 0}
                onClick={handleUpgradeSubscription}
                className="w-full py-3.5 rounded-xl bg-[#6720A4] hover:bg-[#551887] text-white text-xs sm:text-sm font-bold transition-all shadow-md shadow-purple-200 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {upgrading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>{isActive ? "Confirm Upgrade" : "Subscribe Now"}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
